import {
  applyEvent,
  isFreeBallPot,
  type MatchEvent,
  type NewMatchEvent,
  type ScoreState,
} from '@aiscoreboard/contracts';

export interface MatchWriteQueue {
  writeQueue: Promise<void>;
}

export function enqueueMatchWrite<T>(
  owner: MatchWriteQueue,
  write: () => Promise<T>,
  acknowledge?: (request: Promise<T>) => Promise<T>,
): Promise<T> {
  const request = owner.writeQueue.then(write);
  const result = acknowledge ? acknowledge(request) : request;
  owner.writeQueue = result.then(() => undefined, () => undefined);
  return result;
}

export function projectOptimisticState(
  state: ScoreState | null,
  events: readonly MatchEvent[],
): ScoreState | null {
  if (!state || events.length === 0) return state;
  let projected = state;
  for (const [index, event] of events.entries()) {
    projected = applyEvent(projected, { ...event, seq: state.lastSeq + index + 1 });
  }
  return { ...projected, lastSeq: state.lastSeq };
}

export function applyOptimisticEvent(
  state: ScoreState,
  event: MatchEvent,
  sequence: number,
): ScoreState {
  return { ...applyEvent(state, { ...event, seq: sequence }), lastSeq: state.lastSeq };
}

export function rebaseScoringEvent(event: NewMatchEvent, state: ScoreState): NewMatchEvent {
  switch (event.type) {
    case 'shot.pot':
      return {
        ...event,
        player: state.activePlayer,
        freeBall: isFreeBallPot(state, event.color),
      };
    case 'shot.miss':
    case 'shot.foul':
    case 'freeball.awarded':
      return { ...event, player: state.activePlayer };
    default:
      return event;
  }
}

export function latestMatchState(
  current: ScoreState | null,
  incoming: ScoreState,
  matchId: string | null,
): ScoreState | null {
  if (incoming.matchId !== matchId) return current;
  // HTTP responses, socket events and reconnect snapshots can arrive out of order.
  if (current?.matchId === matchId && current.lastSeq >= incoming.lastSeq) return current;
  return incoming;
}
